<!-- EventCreateForm.vue (fixed) -->
<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-md-8">

        <!-- STEP 1: Event Type Picker -->
        <div v-if="step==='type'">
          <div class="page-bar py-3 mb-3">
            <div class="container">
              <div class="d-flex align-items-center">
                <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                  <i class="fas fa-chevron-left"></i>
                </BackButton>
                <div class="page-title flex-grow-1 text-center">Create an Event</div>
              </div>
            </div>
          </div>

          <p class="fw-semibold mb-0">What kind of event are you adding ?</p>
          <p class="mb-3 small text-muted">Whether it’s something exciting coming up or a wonderful memory from the past, let’s get it on the list!</p>

          <div v-if="loadingEventTypes" class="text-center py-5">
            <div class="spinner-border"></div>
          </div>

          <div v-else>
            <button
              v-for="et in eventTypes"
              :key="et.id"
              type="button"
              class="event-type-tile w-100 text-left mb-3"
              :class="{active: selectedEventType && selectedEventType.id===et.id}"
              @click="selectEventType(et)"
            >
              <div class="event-type-tile__body">
                <div class="event-type-tile__copy">
                  <div class="h5 mb-1">{{ et.title }}</div>
                  <div class="mb-1 font-weight-500" style="font-size:13px;">{{ et.subtitle }}</div>
                  <div class="small text-muted">{{ et.description }}</div>
                </div>
                <span class="radio-badge">
                  <span class="dot" v-if="selectedEventType && selectedEventType.id===et.id"></span>
                </span>
              </div>
            </button>

            <div class="text-center mt-4">
              <button
                class="btn btn-primary btn-lg px-4"
                :disabled="!selectedEventType"
                @click="goToForm"
              >
                Proceed to Next
              </button>
            </div>
          </div>
        </div>

        <!-- STEP 2: Form -->
        <div v-else-if="step==='form'">
          <div class="page-bar py-3 mb-3">
            <div class="container">
              <div class="d-flex align-items-center">
                <button class="btn btn-link p-0 mr-2" @click="step='type'">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <div class="page-title flex-grow-1 text-center">{{ (selectedEventType && selectedEventType.title) ? selectedEventType.title : 'Create Event' }}</div>
              </div>
            </div>
          </div>

          <div class="card">
            <form @submit.prevent="submitForm">
              <div class="card-body">
                <!-- Event Title -->
                <div class="form-group mb-3">
                  <input
                    id="eventTitle"
                    type="text"
                    class="form-control"
                    placeholder="Enter event title"
                    v-model.trim="form.title"
                    required
                  />
                </div>

                <!-- Choose Image -->
                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-outline-primary rounded w-100 text-start d-flex justify-content-between align-items-center"
                    @click="toggleImagesCollapse"
                  >
                    <span><i class="fas fa-image mr-2"></i> Choose Image</span>
                    <i class="fas" :class="{'fa-chevron-down': !collapses.imageCollapse,'fa-chevron-up': collapses.imageCollapse}"></i>
                  </button>

                  <div class="collapse mt-2 bg-light p-3 rounded" :class="{ show: collapses.imageCollapse }" id="imageCollapse">
                    <!-- Category Tabs -->
                    <div class="category-tabs overflow-auto d-flex mx-2 mb-3" style="overflow-x:auto !important">
                      <button
                        type="button"
                        v-for="category in categories"
                        :key="category.id"
                        class="btn btn-outline-dark btn-sm p-1 m-1 px-3"
                        style="font-size:12px;white-space:nowrap"
                        :class="{ active: selectedCategory && selectedCategory.id === category.id }"
                        @click="changeCategory(category)"
                      >
                        {{ category.name }}
                      </button>
                    </div>

                    <!-- Type Tabs -->
                    <div class="tabset mb-3" role="tablist">
                      <button type="button" class="tab-btn" :class="{active: currentTab==='Gifs'}" role="tab" :aria-selected="currentTab==='Gifs'" @click="changeTab('Gifs')">
                        GIFs
                      </button>
                      <button type="button" class="tab-btn" :class="{active: currentTab==='Posters'}" role="tab" :aria-selected="currentTab==='Posters'" @click="changeTab('Posters')">
                        Posters
                      </button>
                      <button type="button" class="tab-btn" :class="{active: currentTab==='Upload'}" role="tab" :aria-selected="currentTab==='Upload'" @click="changeTab('Upload')">
                        Upload
                      </button>
                    </div>

                    <!-- GIFs -->
                    <div v-if="currentTab==='Gifs'" class="row mb-3">
                      <div class="col-12 mb-2">
                        <input
                          type="text"
                          class="form-control"
                          v-model.trim="search"
                          placeholder="Search GIFs"
                          @keyup.enter="fetchMedia"
                        />
                      </div>

                      <div class="col-md-3 col-6 mb-2 text-center" v-for="img in filteredMedia" :key="img.id">
                        <img
                          :src="img.url"
                          class="img-thumbnail"
                          :class="{ 'border-primary': isSelected(img) }"
                          style="width:100%;height:100px;object-fit:cover;cursor:pointer"
                          @click="toggleSelect(img)"
                          :alt="`gif-${img.id}`"
                        />
                      </div>
                      <div v-if="filteredMedia.length===0" class="col-12 text-center py-3">
                        <p class="text-muted">No GIFs found</p>
                      </div>
                    </div>

                    <!-- Posters -->
                    <div v-if="currentTab==='Posters'" class="row mb-3">
                      <div class="col-md-3 col-6 mb-2 text-center" v-for="img in filteredMedia" :key="img.id">
                        <img
                          :src="img.image"
                          class="img-thumbnail"
                          :class="{ 'border-primary': isSelected(img) }"
                          style="width:100%;height:100px;object-fit:cover;cursor:pointer"
                          @click="toggleSelect(img)"
                          :alt="`poster-${img.id}`"
                        />
                      </div>
                      <div v-if="filteredMedia.length===0" class="col-12 text-center py-3">
                        <p class="text-muted">No posters found</p>
                      </div>
                    </div>

                    <!-- Upload -->
                    <div v-if="currentTab==='Upload'" class="mb-3">
                      <label for="fileUpload" class="upload-tile d-flex align-items-center justify-content-center">
                        <i class="fas fa-cloud-upload-alt mr-2"></i>
                        <span>Upload from device</span>
                      </label>
                      <input
                        id="fileUpload"
                        type="file"
                        ref="fileInput"
                        multiple
                        @change="handleLocalUpload"
                        accept="image/*"
                        class="sr-only"
                      />
                      <div class="media-grid mt-2 d-flex flex-wrap">
                        <div v-for="(img, index) in selectedLocal" :key="index" class="position-relative m-1">
                          <img v-no-right-click :src="img.preview" class="img-thumbnail" style="width:100px;height:100px;object-fit:cover"/>
                          <button type="button" class="btn btn-danger btn-sm position-absolute del-btn" @click="removeLocalImage(index)" style="top:4px;right:4px;">×</button>
                        </div>
                      </div>
                    </div>

                    <p class="text-muted mt-2">{{ selectedImagesCount }} / 3 selected</p>

                    <div class="d-flex mt-3">
                      <button type="button" @click="addSelectedImages" class="btn btn-primary btn-sm px-3 py-1 mr-2" :disabled="selectedImagesCount===0">
                        Upload Image
                      </button>
                    </div>
                  </div>

                  <!-- Selected previews -->
                  <div class="mt-3 d-flex flex-wrap" v-if="previewImages.length>0">
                    <div v-for="(img, index) in previewImages" :key="index" class="position-relative m-1">
                      <img v-no-right-click :src="img.url || img.preview" class="img-thumbnail rounded" style="width:100px;height:100px;object-fit:cover"/>
                      <button type="button" class="btn btn-warning btn-sm position-absolute del-btn" @click="removeImage(index)" style="top:4px;right:4px;">×</button>
                    </div>
                  </div>
                </div>

                <!-- Set A Date (12-hour format) -->
                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-outline-primary rounded w-100 text-start d-flex justify-content-between align-items-center"
                    @click="toggleCollapse('dateCollapse')"
                  >
                    <span><i class="fas fa-calendar mr-2"></i> Set A Date</span>
                    <i class="fas" :class="{'fa-chevron-down': !collapses.dateCollapse,'fa-chevron-up': collapses.dateCollapse}"></i>
                  </button>
                  <div class="collapse mt-2" :class="{ show: collapses.dateCollapse }" id="dateCollapse">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <span>All-day</span>
                      <label class="switch mb-0">
                        <input type="checkbox" v-model="all_day" aria-label="Toggle all day" />
                        <span class="slider round"></span>
                      </label>
                    </div>
                    <div class="d-flex align-items-center justify-content-between mb-3">
                      <span>Include End Time</span>
                      <label class="switch mb-0">
                        <input type="checkbox" v-model="include_end" aria-label="Toggle include end time" />
                        <span class="slider round"></span>
                      </label>
                    </div>
                    <!-- Starts -->
                    <div class="form-group">
                      <label>Starts:</label>
                      <div class="row flex-wrap align-items-center">
                        <div class="col-md-8 col-7">
                          <input ref="startDateInput" type="text" class="form-control mb-2" placeholder="Select start date" required />
                        </div>
                        <div class="col-md-4 col-5" v-if="!all_day">
                          <input ref="startTimeInput" type="text" class="form-control mb-2" placeholder="Select time" />
                        </div>
                      </div>
                    </div>

                    <!-- Ends -->
                    <div class="form-group" v-if="include_end">
                      <label>Ends:</label>
                      <div class="row flex-wrap align-items-center">
                        <div class="col-md-8 col-7">
                          <input ref="endDateInput" type="text" class="form-control mb-2" placeholder="Select end date" required />
                        </div>
                        <div class="col-md-4 col-5" v-if="!all_day">
                          <input ref="endTimeInput" type="text" class="form-control mb-2" placeholder="Select time" />
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                <!-- Location -->
                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-outline-primary rounded w-100 text-start d-flex justify-content-between align-items-center"
                    @click="toggleCollapse('locationCollapse')"
                  >
                    <span><i class="fas fa-map-marker-alt mr-2"></i> Location</span>
                    <i class="fas" :class="{'fa-chevron-down': !collapses.locationCollapse,'fa-chevron-up': collapses.locationCollapse}"></i>
                  </button>
                  <div class="collapse mt-2" :class="{ show: collapses.locationCollapse }" id="locationCollapse">
                    <div class="form-group">
                      <label for="locationSearch" class="form-label">Search Location</label>
                      <input id="locationSearch" type="text" class="form-control" placeholder="Enter location"
                             v-model="locationSearch" @input="onLocationSearch" ref="locationSearchInput"/>
                      <div v-if="locationSuggestions.length>0" class="list-group mt-2">
                        <button type="button" v-for="(suggestion, index) in locationSuggestions" :key="index"
                                class="list-group-item list-group-item-action" @click="selectLocation(suggestion)">
                          {{ suggestion.description }}
                        </button>
                      </div>
                    </div>

                    <div class="row justify-content-between text-center py-3">
                      <div class="col-md-6 col-6 mb-2 mb-md-0">
                        <button class="btn btn-primary px-3 py-1 w-100" type="button" @click="getCurrentLocation" :disabled="gettingLocation">
                          <span v-if="gettingLocation" class="spinner-border spinner-border-sm mr-2"></span>
                          <i v-else class="fas fa-location-arrow mr-2"></i>
                          {{ gettingLocation ? 'Getting Location...' : 'Use My Current Location' }}
                        </button>
                      </div>
                      <div class="col-md-6 col-6">
                        <button class="btn btn-outline-primary px-3 py-1 w-100" type="button" @click="showManualAddress=!showManualAddress">
                          <i class="fas fa-plus-circle mr-2"></i>
                          {{ showManualAddress ? 'Hide Address' : 'Add Address Manually' }}
                        </button>
                      </div>
                    </div>

                    <div v-if="showManualAddress" class="manual-address-fields">
                      <div class="form-group">
                        <label for="eventPlace" class="form-label">Event Place</label>
                        <input id="eventPlace" type="text" class="form-control" placeholder="Enter event place" v-model.trim="form.event_place"/>
                      </div>
                      <div class="form-group">
                        <label for="doorNo" class="form-label">Flat / H.No / Floor / Building</label>
                        <input id="doorNo" type="text" class="form-control" placeholder="Enter details" v-model.trim="form.door_no"/>
                      </div>
                      <div class="form-group">
                        <label for="locality" class="form-label">Area / Sector / Locality</label>
                        <input id="locality" type="text" class="form-control" placeholder="Enter area/locality" v-model.trim="form.locality"/>
                      </div>
                    </div>

                    <input type="hidden" v-model.trim="form.lat">
                    <input type="hidden" v-model.trim="form.lng">
                  </div>
                </div>

                <!-- Hosted By -->
                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-outline-primary rounded w-100 text-start d-flex justify-content-between align-items-center"
                    @click="toggleCollapse('hostedCollapse')"
                  >
                    <span><i class="fas fa-user-tie mr-2"></i> Hosted By</span>
                    <i class="fas" :class="{'fa-chevron-down': !collapses.hostedCollapse,'fa-chevron-up': collapses.hostedCollapse}"></i>
                  </button>

                  <div class="collapse mt-2" :class="{ show: collapses.hostedCollapse }" id="hostedCollapse">
                    <div class="form-group">
                      <label class="form-label">Name</label>
                      <input type="text" class="form-control" v-model="hostNameDisplay">
                    </div>

                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <label class="mb-0">Co-hosts</label>
                      <button type="button" class="btn btn-sm btn-outline-primary" @click="openCohostModal">
                        Add/Manage Co-hosts
                      </button>
                    </div>

                    <!-- Selected Chips -->
                    <div class="d-flex flex-wrap mb-3" v-if="selectedCohosts.length>0">
                      <div v-for="cohost in selectedCohosts" :key="cohost.id" class="cohost-chip rounded-pill d-flex align-items-center p-2 m-1">
                        <img v-no-right-click :src="cohost.user_img || 'https://dummyimage.com/200x200/222/fff.png'" class="rounded-circle mr-2" style="width:25px;height:25px;object-fit:cover;">
                        <span class="small">{{ capitalize(cohost.name) }}</span>
                        <button type="button" class="btn btn-sm btn-link text-danger p-0 ml-2" @click="removeCohost(cohost)">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Description -->
                <div class="form-group mb-3">
                  <textarea id="eventDescription" class="form-control" rows="5" placeholder="Add a detailed description of your event" v-model.trim="form.description"></textarea>
                </div>

                <div class="event-settings-card" @click="showSettingsModal=true">
                  <div>
                    <div class="font-weight-bold mb-1">Event Settings</div>
                    <div class="small text-muted">
                      Allow attendees to share comments and suggestions after the event to help improve future events.
                    </div>
                  </div>
                  <i class="fas fa-chevron-right text-muted"></i>
                </div>
              </div>

              <div class="card-footer text-center">
                <div class="d-grid">
                  <button type="submit" class="btn btn-primary btn-lg mx-auto" style="min-width:150px" :disabled="isSubmitting">
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm mr-2"></span>
                    {{ isSubmitting ? 'Creating...' : 'Create' }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Settings modal -->
  <div v-if="showSettingsModal" class="modal-backdrop-custom settings-backdrop" @click.self="showSettingsModal=false">
    <div class="settings-modal-card" role="dialog" aria-modal="true" tabindex="-1">
      <div class="settings-modal-header">
        <button class="btn btn-link p-0 mr-2 text-dark" @click="showSettingsModal=false" aria-label="Close settings">
          <i class="fas fa-chevron-left"></i>
        </button>
        <div class="flex-grow-1 text-center font-weight-bold">Event Settings</div>
        <div style="width:16px;"></div>
      </div>

      <div class="settings-modal-body settings-soft-panel">
        <div class="settings-section settings-soft-panel">
          <div class="settings-section-title">RSVP & Joining</div>
          <p class="settings-section-sub">Decide how people can participate</p>

          <div class="settings-tile">
            <div>
              <div class="settings-tile-title">Accept RSVP</div>
              <div class="settings-tile-sub">Guests can RSVP (Yes / No / Maybe) if enabled.</div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="settings.acceptRsvp" />
              <span class="slider round"></span>
            </label>
          </div>

          <div class="settings-tile" :class="{ 'disabled-tile': !settings.acceptRsvp }">
            <div>
              <div class="settings-tile-title">Guest Count (Adults/Children)</div>
              <div class="settings-tile-sub">Let guests set how many are coming.</div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="settings.guestCount" :disabled="!settings.acceptRsvp" />
              <span class="slider round"></span>
            </label>
          </div>
        </div>

        <div class="settings-section settings-soft-panel">
          <div class="settings-section-title">Album</div>
          <p class="settings-section-sub">Enable album to share event photos</p>

          <div class="settings-tile">
            <div>
              <div class="settings-tile-title">Album</div>
              <div class="settings-tile-sub">
                Capture and share memories effortlessly with our photo booth module, offering secure and customizable image and video sharing post-event.
              </div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="form.album_enabled" />
              <span class="slider round"></span>
            </label>
          </div>
        </div>

        <div class="settings-section settings-soft-panel">
          <div class="settings-section-title">Feedback</div>
          <p class="settings-section-sub">Let guests share how it went</p>

          <div class="settings-tile">
            <div>
              <div class="settings-tile-title">Enable Feedback</div>
              <div class="settings-tile-sub">
                Let your guests share their thoughts about the event. Enable this option to collect private feedback from attendees, helping you understand what they enjoyed and what could be improved for future events.
              </div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="form.feedback" />
              <span class="slider round"></span>
            </label>
          </div>

          <div class="settings-tile" :class="{'disabled-tile': !form.feedback}">
            <div>
              <div class="settings-tile-title">Enable Rating</div>
              <div class="settings-tile-sub">
                Enable guests to respond whether they will attend the event.
              </div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="form.rating" :disabled="!form.feedback" />
              <span class="slider round"></span>
            </label>
          </div>
        </div>

        <AttachmentOptions v-model="attachments" class="mb-3" />
      </div>
    </div>
  </div>

  <!-- Cohost Modal -->
  <div v-if="showCohostModal" class="modal-backdrop-custom" @click.self="cancelCohostModal">
    <div class="modal-card" ref="modalCard" role="dialog" aria-modal="true" tabindex="-1">
      <div class="modal-header">
        <h5 class="modal-title">Select Co-hosts</h5>
        <button class="close" @click="closeCohostModal" aria-label="Close">&times;</button>
      </div>
      <div class="modal-body">
        <div v-if="cohosts.length===0" class="text-center py-4 text-muted">No friends found.</div>
        <ul class="list-group list-group-flush" v-else>
          <li class="list-group-item p-3" v-for="cohost in cohosts" :key="cohost.id">
            <div class="d-flex align-items-center">
              <img v-no-right-click :src="cohost.user_img || 'https://dummyimage.com/200x200/222/fff.png'" class="rounded-circle mr-3" style="width:44px;height:44px;object-fit:cover">
              <div class="flex-grow-1">
                <div class="fw-semibold">{{ capitalize(cohost.name) }}</div>
                <div class="small text-muted">@{{ cohost.username }}</div>
              </div>
              <button type="button" class="btn btn-sm" :class="isCohostSelected(cohost)?'btn-secondary':'btn-success'" @click="toggleCohost(cohost)">
                {{ isCohostSelected(cohost) ? 'Selected' : 'Select' }}
              </button>
            </div>
          </li>
        </ul>
      </div>
      <div class="modal-footer">
        <button class="btn btn-outline-secondary" @click="closeCohostModal">Cancel</button>
        <button class="btn btn-primary" @click="saveCohostModal">Done</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import api from "@/services/api";
import { useSessionStore } from "@/stores/session";
import Swal from "sweetalert2";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import { useRouter } from 'vue-router'
import BackButton from '@/components/common/BackButton.vue'
import AttachmentOptions from '@/components/common/AttachmentOptions.vue'

/* Google Maps loader */
const loadGoogleMapsAPI = () =>
  new Promise((resolve, reject) => {
    if (window.google && window.google.maps) return resolve();
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const t = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(t);
          resolve();
        }
      }, 100);
      return;
    }
    const script = document.createElement("script");
    const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY || "YOUR_API_KEY";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps API"));
    document.head.appendChild(script);
  });

const toast = (msg, icon = "success") =>
  Swal.fire({ toast: true, position: "top-end", icon, title: msg, showConfirmButton: false, timer: 2000 });

const alertErr = (msg) => Swal.fire({ icon: "error", title: "Oops…", text: msg });

export default {
  name: "EventCreateForm",
  components: { BackButton, AttachmentOptions },
  setup() {
    const router = useRouter();
    const session = useSessionStore();

    /* ---------- Step handling ---------- */
    const step = ref("type");
    const eventTypes = ref([]);
    const selectedEventType = ref(null);
    const normalizedEventType = computed(() => {
      const rawType = selectedEventType.value?.type ?? "";
      return String(rawType).trim().toLowerCase();
    });
    const isNewEvent = computed(() => normalizedEventType.value === "new");
    const isPastEvent = computed(() =>
      normalizedEventType.value === "old" || normalizedEventType.value === "past"
    );
    const loadingEventTypes = ref(true);

    const goToForm = async () => {
      if (!selectedEventType.value) {
        toast("Please select an event type first", "warning");
        return;
      }
      step.value = "form";
      await nextTick();
      initDatePickers();
      updatePickerDisabledStates();
    };

    const selectEventType = (et) => {
      selectedEventType.value = et;
    };

    const fetchEventTypes = async () => {
      try {
        loadingEventTypes.value = true;
        const { data } = await api.getEventTypes();
        eventTypes.value = data?.data || [
          { id: 1, title: "New Events", description: "Use this option to create a brand-new event.", type: "new" },
          { id: 2, title: "Past Events", description: "Select this option to view or edit an existing event.", type: "old" },
        ];
      } catch (e) {
        console.error("getEventTypes failed", e);
        eventTypes.value = [
          { id: 1, title: "New Events", description: "Use this option to create a brand-new event.", type: "new" },
          { id: 2, title: "Past Events", description: "Select this option to view or edit an existing event.", type: "old" },
        ];
      } finally {
        loadingEventTypes.value = false;
      }
    };

    /* ---------- Date/Time (12-hour) ---------- */
    const all_day = ref(false);
    const include_end = ref(true); // default checked
    const start_date = ref("");
    const start_time = ref("12:00 PM");
    const end_date = ref("");
    const end_time = ref("12:00 PM");

    const startDateInput = ref(null);
    const endDateInput = ref(null);
    const startTimeInput = ref(null);
    const endTimeInput = ref(null);

    const fp = { startDate: null, endDate: null, startTime: null, endTime: null };

    // capture current values safely
    function snapshotPickerState() {
      return {
        sDate: start_date.value,
        eDate: end_date.value,
        sTime: start_time.value,
        eTime: end_time.value,
      };
    }

    async function rebuildPickersOnStructureChange() {
      // Keep current values
      const snap = snapshotPickerState();

      // Tear down old instances (their DOM may have been removed by v-if)
      destroyPickers();

      // Wait for DOM to reflect the new v-if/v-show state
      await nextTick();

      // Restore values if they got blanked by DOM changes
      start_date.value = snap.sDate || start_date.value;
      end_date.value   = snap.eDate   || end_date.value;
      start_time.value = snap.sTime   || start_time.value || "12:00 PM";
      end_time.value   = snap.eTime   || end_time.value   || "12:00 PM";

      // Re-init with the restored values
      initDatePickers();

      // Re-apply enable/disable and min/max constraints
      updatePickerDisabledStates();
      syncEndTimeConstraints();

      // Force a visual refresh (sometimes needed after dynamic rebuild)
      if (fp.startDate?.redraw) fp.startDate.redraw();
      if (fp.endDate?.redraw)   fp.endDate.redraw();
      if (fp.startTime?.redraw) fp.startTime.redraw();
      if (fp.endTime?.redraw)   fp.endTime.redraw();
    }



    const destroyPickers = () => {
      Object.keys(fp).forEach(k => {
        if (fp[k]?.destroy) {
          fp[k].destroy();
          fp[k] = null;
        }
      });
    };

    onBeforeUnmount(destroyPickers);

    const initializeDefaultDates = () => {
      const today = ymd(new Date());
      start_date.value = today;
      end_date.value   = today;
      start_time.value = "12:00 PM";
      end_time.value   = "12:00 PM";
    };

    const dateValidation = computed(() => {
      const errors = [];
      const today = new Date().toISOString().split("T")[0];

      if (isNewEvent.value && start_date.value && start_date.value < today) {
        errors.push("Start date cannot be in the past for new events");
      }
      if (isPastEvent.value && start_date.value && start_date.value > today) {
        errors.push("Past events cannot have dates in the future");
      }
      if (isPastEvent.value && include_end.value && end_date.value && end_date.value > today) {
        errors.push("Past events cannot have end dates in the future");
      }
      if (include_end.value && start_date.value && end_date.value && end_date.value < start_date.value) {
        errors.push("End date cannot be before start date");
      }
      return { isValid: errors.length === 0, errors };
    });

    // Keep picker bounds in sync with selected event type
    watch(selectedEventType, (et) => {
      const todayStr = ymd(new Date());
      const type = String(et?.type || "").trim().toLowerCase();
      const isNew = type === "new";
      const isPast = type === "old" || type === "past";

      if (isNew && start_date.value && start_date.value < todayStr) {
        start_date.value = todayStr;
        if (fp.startDate) fp.startDate.setDate(todayStr, false);
      }
      if (isPast && start_date.value && start_date.value > todayStr) {
        start_date.value = todayStr;
        if (fp.startDate) fp.startDate.setDate(todayStr, false);
      }

      if (include_end.value && end_date.value) {
        const minEnd = start_date.value || (isNew ? todayStr : null);
        if (minEnd && end_date.value < minEnd) {
          end_date.value = minEnd;
          if (fp.endDate) fp.endDate.setDate(minEnd, false);
        }
        if (isPast && end_date.value > todayStr) {
          end_date.value = todayStr;
          if (fp.endDate) fp.endDate.setDate(todayStr, false);
        }
      }

      if (fp.startDate) {
        fp.startDate.set("minDate", isNew ? todayStr : null);
        fp.startDate.set("maxDate", isPast ? todayStr : null);
      }
      if (fp.endDate) {
        const min = start_date.value || (isNew ? todayStr : null);
        fp.endDate.set("minDate", min);
        fp.endDate.set("maxDate", isPast ? todayStr : null);
      }
    });

    // Keep End date >= Start date
    watch(start_date, (s) => {
      if (fp.endDate) fp.endDate.set("minDate", s || null);
      if (include_end.value) {
        if (!end_date.value || end_date.value < s) {
          end_date.value = s;
          if (fp.endDate) fp.endDate.setDate(s, false);
        }
      }
      syncEndTimeConstraints();
    });

    watch(end_date, () => { syncEndTimeConstraints(); });

    // Respect toggles
    watch(all_day, () => {
      rebuildPickersOnStructureChange();
      updatePickerDisabledStates();
      syncEndTimeConstraints();
    });
    watch(include_end, () => {
      rebuildPickersOnStructureChange();
      updatePickerDisabledStates();
      syncEndTimeConstraints();
    });

    /* ---------- Form state ---------- */
    const form = reactive({
      title: "",
      location: "",
      hosted_by: "",
      description: "",
      album_enabled: false,
      feedback: false,
      rating: false,
      images: [],
      lat: "",
      lng: "",
      event_place: "",
      door_no: "",
      locality: "",
      address: "",
    });

    const settings = reactive({
      acceptRsvp: true,
      guestCount: false,
    });

    const attachments = reactive({
      textPhoto: false,
      schedule: false,
      poll: false,
      carPool: false
    });

    const showSettingsModal = ref(false);

    watch(() => form.feedback, (val) => {
      if (!val) form.rating = false;
    });
    watch(
      () => settings.acceptRsvp,
      (val) => {
        if (!val) settings.guestCount = false;
      }
    );

    const toFlag = (val) => (val ? "1" : "0");

    const collapses = reactive({
      imageCollapse: false,
      dateCollapse: false,
      locationCollapse: false,
      hostedCollapse: false,
    });

    const isSubmitting = ref(false);
    const fileInput = ref(null);
    const previewImages = ref([]);

    // Location
    const locationSearch = ref("");
    const locationSuggestions = ref([]);
    const showManualAddress = ref(false);
    const gettingLocation = ref(false);
    const autocompleteService = ref(null);
    const placesService = ref(null);
    const geocoder = ref(null);

    // Media
    const search = ref("");
    const currentTab = ref("Posters"); // default Posters
    const selectedCategory = ref(null);
    const categories = ref([]);
    const allMedia = ref([]);
    const selected = ref([]);
    const selectedLocal = ref([]);

    // Cohosts
    const cohosts = ref([]);
    const selectedCohosts = ref([]);
    const showCohostModal = ref(false);
    const modalCard = ref(null);
    const snapshotSelected = ref([]);
    let keydownHandler = null;

    // Session / user
    const user = reactive({ id: "", name: "", username: "", email: "" });
    const hostNameDisplay = ref("");

    const fetchProfile = async () => {
      if (!session.userId) return;
      try {
        const fd = new FormData();
        fd.append("id", session.userId);
        const { data } = await api.getProfileApi(fd);
        if (data?.status === "ok" && data?.data?.[0]) {
          Object.assign(user, data.data[0]);
          form.hosted_by = user.username || user.name || "";
          hostNameDisplay.value = user.name || user.username || "";
        }
      } catch (e) {
        console.error("Profile fetch failed", e);
      }
    };

    const toggleCollapse = (k) => { collapses[k] = !collapses[k]; };

    // Ensures Posters tab is active and media loaded when opening the Images collapse
    const toggleImagesCollapse = async () => {
      collapses.imageCollapse = !collapses.imageCollapse;
      if (collapses.imageCollapse) {
        if (currentTab.value !== "Posters") currentTab.value = "Posters";
        if (!categories.value.length) await fetchCategories();
        if (!selectedCategory.value && categories.value.length) selectedCategory.value = categories.value[0];
        await fetchMedia();
      }
    };

    const resetForm = () => {
      Object.assign(form, {
        title: "",
        location: "",
        hosted_by: "",
        description: "",
        album_enabled: false,
        feedback: false,
        rating: false,
        images: [],
        lat: "",
        lng: "",
        event_place: "",
        door_no: "",
        locality: "",
        address: "",
      });
      previewImages.value = [];
      selected.value = [];
      selectedLocal.value = [];
      selectedCohosts.value = [];
      locationSearch.value = "";
      locationSuggestions.value = [];
      showManualAddress.value = false;
      if (fileInput.value) fileInput.value.value = "";
      search.value = "";
      all_day.value = false;
      include_end.value = true;
      settings.acceptRsvp = true;
      settings.guestCount = false;
      showSettingsModal.value = false;
      initializeDefaultDates();
      updatePickerDisabledStates();
    };

    const removeImage = (index) => {
      previewImages.value.splice(index, 1);
      if (form.images.length > index) {
        form.images.splice(index, 1);
      }
    };

    const removeLocalImage = (index) => {
      selectedLocal.value.splice(index, 1);
    };

    const getCoHosts = async () => {
      try {
        const fd = new FormData();
        fd.append("userid", session.userId);
        const res = await api.getMyFriends(fd);
        const list = res?.data?.data || res?.data?.list || res?.data || [];
        cohosts.value = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error("Error loading cohosts:", e);
        cohosts.value = [];
      }
    };

    const atImageLimit = () =>
      selected.value.length + selectedLocal.value.length + previewImages.value.length >= 3;

    const toggleSelect = (img) => {
      if (currentTab.value === "Upload") return;
      const exists = selected.value.some((s) => s.id === img.id);
      if (exists) {
        selected.value = selected.value.filter((s) => s.id !== img.id);
      } else {
        if (atImageLimit()) {
          toast("Maximum 3 images allowed", "warning");
          return;
        }
        selected.value.push(img);
      }
    };

    const handleLocalUpload = (e) => {
      const files = Array.from(e.target.files || []);
      for (const f of files) {
        if (atImageLimit()) {
          toast("Maximum 3 images allowed", "warning");
          break;
        }
        if (!f.type.startsWith("image/")) continue;
        const preview = URL.createObjectURL(f);
        selectedLocal.value.push({ file: f, preview, isLocal: true });
      }
      e.target.value = "";
    };

    const addSelectedImages = () => {
      const toAdd = [];

      // Remote first
      selected.value.forEach((r) => {
        if (previewImages.value.length + toAdd.length >= 3) return;
        toAdd.push({
          isRemote: true,
          url: r.url || r.image,
          id: r.id
        });
      });

      // Local files
      selectedLocal.value.forEach((l) => {
        if (previewImages.value.length + toAdd.length >= 3) return;
        toAdd.push({
          isLocal: true,
          file: l.file,
          preview: l.preview
        });
      });

      if (!toAdd.length) {
        toast("Nothing to add (or limit reached)", "warning");
        return;
      }

      previewImages.value.push(...toAdd);

      // Keep form.images in sync
      form.images = previewImages.value.map(img =>
        img.isRemote ? img.url : img.file
      );

      selected.value = [];
      selectedLocal.value = [];
      collapses.imageCollapse = false;
      toast("Images added", "success");
    };

    /* ---------- Cohost modal ---------- */
    const lockBody = () => { document.documentElement.style.overflow = 'hidden'; };
    const unlockBody = () => { document.documentElement.style.overflow = ''; };

    const onKeydown = (e) => { if (e.key === 'Escape') cancelCohostModal(); };

    const openCohostModal = () => {
      snapshotSelected.value = [...selectedCohosts.value];
      showCohostModal.value = true;
      lockBody();
      keydownHandler = onKeydown;
      window.addEventListener('keydown', keydownHandler);
      nextTick(() => { if (modalCard.value) modalCard.value.focus(); });
    };

    const cancelCohostModal = () => {
      selectedCohosts.value = [...snapshotSelected.value];
      closeCohostModal();
    };

    const closeCohostModal = () => {
      showCohostModal.value = false;
      unlockBody();
      if (keydownHandler) {
        window.removeEventListener('keydown', keydownHandler);
        keydownHandler = null;
      }
    };

    const saveCohostModal = () => {
      snapshotSelected.value = [...selectedCohosts.value];
      closeCohostModal();
      toast("Co-hosts updated", "success");
    };

    const toggleCohost = (cohost) => {
      const idx = selectedCohosts.value.findIndex((c) => c.id === cohost.id);
      if (idx > -1) {
        selectedCohosts.value.splice(idx, 1);
      } else {
        selectedCohosts.value.push(cohost);
      }
    };

    const removeCohost = (cohost) => {
      const idx = selectedCohosts.value.findIndex((c) => c.id === cohost.id);
      if (idx > -1) selectedCohosts.value.splice(idx, 1);
    };

    const isCohostSelected = (cohost) =>
      selectedCohosts.value.some((c) => c.id === cohost.id);

    /* ---------- Computeds ---------- */
    const selectedImagesCount = computed(() =>
      selected.value.length + selectedLocal.value.length + previewImages.value.length
    );

    const filteredMedia = computed(() =>
      (currentTab.value === "Upload" ? [] : allMedia.value)
    );

    const isSelected = (img) =>
      currentTab.value !== "Upload" && selected.value.some((s) => s.id === img.id);

    /* ---------- Media fetching ---------- */
    const GIPHY_KEY = process.env.VUE_APP_GIPHY_API_KEY || "aKIJbMMqwwndypRfzeWy9HrVeKNXStCE";

    const fetchCategories = async () => {
      try {
        if (!api.getPosterCategories) return;
        const res = await api.getPosterCategories();
        const list = res?.data?.list || res?.data?.data || res?.data || [];
        categories.value = Array.isArray(list) ? list : [];
        if (!selectedCategory.value && categories.value.length) {
          selectedCategory.value = categories.value[0];
        }
      } catch (e) {
        console.error("getPosterCategories failed", e);
        categories.value = [];
      }
    };

    const fetchMedia = async () => {
      try {
        if (currentTab.value === "Gifs") {
          const q = (search.value || selectedCategory.value?.name || "fun").trim();
          let gData = null;
          if (api.getGiphy) {
            try {
              const params = { api_key: GIPHY_KEY, q, limit: 24, rating: "pg" };
              const res = await api.getGiphy(params);
              gData = res?.data?.data || res?.data || null;
            } catch (e) {
              console.error("getGiphy error:", e);
            }
          }
          if (!gData) {
            const url = `https://api.giphy.com/v1/gifs/search?api_key=${encodeURIComponent(GIPHY_KEY)}&q=${encodeURIComponent(q)}&limit=24&rating=pg&lang=en`;
            const res = await fetch(url);
            const json = await res.json();
            gData = json?.data || [];
          }
          allMedia.value = (gData || [])
            .map((g) => ({
              id: g.id,
              url: g.images?.fixed_width?.url || g.images?.downsized_medium?.url || g.images?.original?.url || "",
            }))
            .filter((x) => x.url);
        } else if (currentTab.value === "Posters") {
          if (!selectedCategory.value && categories.value.length) {
            selectedCategory.value = categories.value[0];
          }
          const fd = new FormData();
          fd.append("category", selectedCategory.value?.id || "");
          const res = await api.getPosters(fd);
          const list = res?.data?.list || [];
          allMedia.value = list.map((p) => ({ id: p.id, image: p.image })).filter((p) => p.image);
        } else {
          allMedia.value = [];
        }
      } catch (e) {
        console.error("Error fetching media:", e);
        allMedia.value = [];
      }
    };

    const changeTab = async (tab) => {
      currentTab.value = tab;
      selected.value = [];
      if (tab !== "Upload") {
        if (tab === "Posters" && !categories.value.length) await fetchCategories();
        await fetchMedia();
      }
    };

    const changeCategory = async (cat) => {
      selectedCategory.value = cat;
      if (currentTab.value !== "Upload") await fetchMedia();
    };

    // Debounced live search for GIFs
    let gifDebounce;
    watch(search, () => {
      if (currentTab.value !== "Gifs") return;
      clearTimeout(gifDebounce);
      gifDebounce = setTimeout(() => {
        fetchMedia();
      }, 400);
    });

    /* ---------- Date helpers (12-hour safe) ---------- */
    const ensureAmPm = (val) => {
      if (!val) return "";
      const s = String(val).trim();
      if (/[AP]M$/i.test(s)) return s.toUpperCase();
      const [hStr, m = "00"] = s.split(":");
      if (isNaN(+hStr)) return s;
      let h = parseInt(hStr, 10);
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;
      return `${h}:${m} ${ampm}`;
    };

    // Payload rules for 4 cases:
    // 1) !all_day && include_end: fromdate, todate, fromtime, totime
    // 2)  all_day && include_end: fromdate, todate, times empty
    // 3)  all_day && !include_end: fromdate only; todate/fromtime/totime empty
    // 4) !all_day && !include_end: fromdate, fromtime only; todate/totime empty

    /* ---------- Build payload dates (EXACT rules you asked) ---------- */
    const buildDatesForPayload = () => {
      const today = new Date().toISOString().split("T")[0];
      let fromdate = start_date.value || today;

      // Prevent past start date for new events
      if (isNewEvent.value && fromdate < today) {
        fromdate = today;
      }
      if (isPastEvent.value && fromdate > today) {
        fromdate = today;
      }

      let todate = "";
      let fromtime = "";
      let totime = "";

      if (all_day.value && !include_end.value) {
        // 🟩 Case 1: Single all-day event
        todate = fromdate;

      } else if (all_day.value && include_end.value) {
        // 🟩 Case 4: Multi-day all-day event
        todate = end_date.value && end_date.value >= fromdate ? end_date.value : fromdate;

      } else if (!all_day.value && include_end.value) {
        // 🟩 Case 5: Multi-day timed event
        todate = end_date.value && end_date.value >= fromdate ? end_date.value : fromdate;
        fromtime = ensureAmPm(start_time.value);
        totime = ensureAmPm(end_time.value);

      } else if (!all_day.value && !include_end.value) {
        // 🟩 Case 2: Single date + single start time
        todate = fromdate;
        fromtime = ensureAmPm(start_time.value);
        // totime = fromtime; // same start/end time
        totime = "";
      }

      if (isPastEvent.value && todate && todate > today) {
        todate = today;
      }

      return { fromdate, todate, fromtime, totime };
    };


    /* ---------- Maps ---------- */
    const onLocationSearch = () => {
      if (!autocompleteService.value || !locationSearch.value) {
        locationSuggestions.value = [];
        return;
      }
      autocompleteService.value.getPlacePredictions({ input: locationSearch.value }, (preds, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) {
          locationSuggestions.value = preds;
        } else {
          locationSuggestions.value = [];
        }
      });
    };

    const selectLocation = (suggestion) => {
      locationSearch.value = suggestion.description;
      locationSuggestions.value = [];
      placesService.value.getDetails({ placeId: suggestion.place_id }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          form.lat = place.geometry.location.lat();
          form.lng = place.geometry.location.lng();
          form.address = place.formatted_address;
          extractAddressComponents(place.address_components);
        }
      });
    };

    const extractAddressComponents = (ac) => {
      if (!ac) return;
      ac.forEach((c) => {
        const t = c.types || [];
        if (t.includes("street_number")) form.door_no = c.long_name;
        else if (t.includes("route")) form.event_place = c.long_name;
        else if (t.includes("locality") || t.includes("sublocality")) form.event_place = c.long_name;
        else if (t.includes("administrative_area_level_2") && !form.event_place) form.event_place = c.long_name;
      });
    };

    const getCurrentLocation = () => {
      if (!navigator.geolocation) {
        alertErr("Geolocation is not supported by your browser");
        return;
      }
      gettingLocation.value = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude, lng = pos.coords.longitude;
          form.lat = lat;
          form.lng = lng;
          if (geocoder.value) {
            geocoder.value.geocode({ location: { lat, lng } }, (results, status) => {
              gettingLocation.value = false;
              if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
                const address = results[0].formatted_address;
                locationSearch.value = address;
                form.address = address;
                extractAddressComponents(results[0].address_components);
              } else {
                alertErr("Could not get address from your location");
              }
            });
          } else {
            gettingLocation.value = false;
          }
        },
        (err) => {
          gettingLocation.value = false;
          alertErr(`Error getting location: ${err.message}`);
        },
        { timeout: 10000 }
      );
    };

    // ---- time/date helpers ----
    const ymd = (d) => {
      const dt = (d instanceof Date) ? d : new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    };

    function parseTimeToMinutes(str) {
      if (!str) return null;
      const s = String(str).trim().toUpperCase();
      const m = s.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const ap = m[3];
      if (ap) {
        const isPM = ap === 'PM';
        if (h === 12) h = isPM ? 12 : 0;
        else if (isPM) h += 12;
      }
      return (h * 60) + min;
    }

    function cmpTimes(a, b) {
      const A = parseTimeToMinutes(a), B = parseTimeToMinutes(b);
      if (A == null || B == null) return 0;
      return A - B;
    }

    function setTimeOnFp(instance, timeStr) {
      if (!instance || !timeStr) return;
      instance.setDate(timeStr, false, "h:i K");
    }

    let suppressEndChange = false;

    function syncEndTimeConstraints() {
      if (all_day.value || !include_end.value) {
        if (fp.endTime) fp.endTime.set('minTime', null);
        return;
      }

      const sameDay = !!start_date.value && !!end_date.value && (start_date.value === end_date.value);

      if (fp.endTime) {
        if (sameDay) {
          const minT = start_time.value || "12:00 PM";
          fp.endTime.set('minTime', minT);

          if (end_time.value && cmpTimes(end_time.value, minT) < 0) {
            suppressEndChange = true;
            end_time.value = start_time.value || "12:00 PM";
            setTimeOnFp(fp.endTime, end_time.value);
            suppressEndChange = false;
          }
        } else {
          fp.endTime.set('minTime', null);
        }
      }
    }

    function updatePickerDisabledStates() {
      // Start Time enabled only if not all-day
      if (startTimeInput.value) startTimeInput.value.disabled = !!all_day.value;
      if (fp.startTime) {
        if (all_day.value) {
          start_time.value = "12:00 PM"; // keep a sane value but disabled in UI
        }
        setTimeOnFp(fp.startTime, start_time.value);
      }

      // End Date/Time visible only if include_end
      if (endDateInput.value) endDateInput.value.disabled = !include_end.value;
      if (endTimeInput.value) endTimeInput.value.disabled = !!all_day.value || !include_end.value;

      if (!include_end.value) {
        // Clear end_date/time when disabled
        end_time.value = "12:00 PM";
        // keep end_date equal to start_date for UI continuity, but it won't be sent if include_end=false
        end_date.value = start_date.value || end_date.value;
        if (fp.endTime) setTimeOnFp(fp.endTime, end_time.value);
        if (fp.endDate && start_date.value) fp.endDate.setDate(start_date.value, false);
      }

      if (include_end.value) {
        // Ensure min/max Date and default end_date
        if (fp.endDate) {
          const todayStr = ymd(new Date());
          const min = start_date.value || (isNewEvent.value ? todayStr : null);
          const max = isPastEvent.value ? todayStr : null;
          fp.endDate.set("minDate", min);
          fp.endDate.set("maxDate", max);
          if (!end_date.value) {
            end_date.value = start_date.value || min;
            fp.endDate.setDate(end_date.value, false);
          }
        }
      }
    }

    /* ---------- Init Flatpickr (12-hour) ---------- */
    const initDatePickers = () => {
      destroyPickers();

      const isNew = isNewEvent.value;
      const isPast = isPastEvent.value;
      const todayStr = ymd(new Date());

      // START DATE
      if (startDateInput.value) {
        fp.startDate = flatpickr(startDateInput.value, {
          dateFormat: "Y-m-d",
          allowInput: true,
          disableMobile: true,
          minDate: isNew ? todayStr : null,
          maxDate: isPast ? todayStr : null,
          defaultDate: start_date.value || (isNew ? todayStr : null),
          onChange: (dates) => {
            if (!dates[0]) return;
            const s = ymd(dates[0]);
            start_date.value = s;

            // End date cannot be before Start
            if (fp.endDate) {
              fp.endDate.set("minDate", s || (isNew ? todayStr : null));
              fp.endDate.set("maxDate", isPast ? todayStr : null);
            }
            if (include_end.value && (!end_date.value || end_date.value < s)) {
              suppressEndChange = true;
              end_date.value = s;
              if (fp.endDate) fp.endDate.setDate(s, false);
              suppressEndChange = false;
            }
            syncEndTimeConstraints();
          },
          onOpen: () => {
            const now = ymd(new Date());
            if (fp.startDate) {
              fp.startDate.set("minDate", isNew ? now : null);
              fp.startDate.set("maxDate", isPast ? now : null);
            }
          }
        });
      }

      // END DATE
      if (endDateInput.value) {
        const startForMin = start_date.value || (isNew ? todayStr : null);

        fp.endDate = flatpickr(endDateInput.value, {
          dateFormat: "Y-m-d",
          allowInput: false,
          disableMobile: true,
          minDate: startForMin,
          maxDate: isPast ? todayStr : null,
          defaultDate: end_date.value || startForMin,
          disable: [
            (date) => {
              if (!start_date.value) return false;
              const sd = new Date(start_date.value);
              sd.setHours(0,0,0,0);
              const d  = new Date(date);
              d.setHours(0,0,0,0);
              return d < sd;
            }
          ],
          onOpen: () => {
            const now = ymd(new Date());
            fp.endDate.set("minDate", start_date.value || (isNew ? now : null));
            fp.endDate.set("maxDate", isPast ? now : null);
          },
          onChange: (dates) => {
            if (!dates[0] || suppressEndChange) return;
            const picked = ymd(dates[0]);
            const min = start_date.value || picked;
            if (picked < min) {
              suppressEndChange = true;
              end_date.value = min;
              fp.endDate.setDate(min, false);
              suppressEndChange = false;
            } else {
              end_date.value = picked;
            }
            syncEndTimeConstraints();
          }
        });
      }

      // START TIME (12h)
      if (startTimeInput.value) {
        startTimeInput.value.disabled = !!all_day.value;
        fp.startTime = flatpickr(startTimeInput.value, {
          enableTime: true,
          noCalendar: true,
          dateFormat: "h:i K",
          time_24hr: false,
          disableMobile: true,
          defaultDate: start_time.value || "12:00 PM",
          onChange: (_d, str) => {
            start_time.value = str || "12:00 PM";
            syncEndTimeConstraints();
          }
        });
      }

      // END TIME (12h)
      if (endTimeInput.value) {
        endTimeInput.value.disabled = !!all_day.value || !include_end.value;
        fp.endTime = flatpickr(endTimeInput.value, {
          enableTime: true,
          noCalendar: true,
          dateFormat: "h:i K",
          time_24hr: false,
          disableMobile: true,
          defaultDate: end_time.value || "12:00 PM",
          onChange: (_d, str) => {
            if (suppressEndChange) return;
            end_time.value = str || "12:00 PM";
            syncEndTimeConstraints();
          }
        });
      }

      updatePickerDisabledStates();
      syncEndTimeConstraints();
    };

    /* ---------- Submit ---------- */
    const submitForm = async () => {
      if (isSubmitting.value) return;
      if (!form.title.trim()) return alertErr("Please enter an event title");
      if (!start_date.value) return alertErr("Please select a start date");
      if (include_end.value && !end_date.value) return alertErr("Please select an end date");
      if (!form.address && !form.event_place && !form.locality) return alertErr("Please provide a location");
      if (!selectedEventType.value) return alertErr("Please select an event type");
      if (!dateValidation.value.isValid) return alertErr(dateValidation.value.errors.join(", "));

      try {
        isSubmitting.value = true;

        const remoteUrls = [];
        const localFiles = [];
        previewImages.value.forEach((pi) => {
          if (pi.isRemote && pi.url) remoteUrls.push(pi.url);
          else if (pi.isLocal && pi.file instanceof File) localFiles.push(pi.file);
        });

        const maxTotal = Math.min(3, remoteUrls.length + localFiles.length);
        const urlsToSend = remoteUrls.slice(0, Math.min(remoteUrls.length, maxTotal));
        const remaining = maxTotal - urlsToSend.length;
        const filesToSend = localFiles.slice(0, remaining);

        const fd = new FormData();
        const { fromdate, todate, fromtime, totime } = buildDatesForPayload();

        fd.append("id", session.userId || "");
        fd.append("eventid", "");
        fd.append("orgname", form.title || "");
        fd.append("hostname", hostNameDisplay.value || "");
        fd.append("description", form.description || "");
        fd.append("fromdate", fromdate); 
        fd.append("todate", todate || '');
        fd.append("fromtime", fromtime || '');
        fd.append("totime", totime || '');
        fd.append("all_day", all_day.value ? 'yes' : 'no');
        fd.append("include_end_time", include_end.value ? 'yes' : 'no');


        const addressStr = form.address || `${form.event_place || ""}${form.locality ? (form.event_place ? ", " : "") + form.locality : ""}`;
        fd.append("address", addressStr);
        fd.append("event_place", form.event_place || "");
        fd.append("door_no", form.door_no || "");
        fd.append("locality", form.locality || "");
        fd.append("lat", form.lat || "");
        fd.append("lng", form.lng || "");
        fd.append("mode_id", "2");
        fd.append("type", "8");
        fd.append("subtype", "");
        fd.append("event_type", selectedEventType.value?.type || "new");
        fd.append("photo_booth", form.album_enabled ? "yes" : "no");
        fd.append("feedback", form.feedback ? "yes" : "no");
        fd.append("rating", form.feedback && form.rating ? "yes" : "no");
        fd.append("host_ids", selectedCohosts.value.map((c) => c.id).join(","));
        fd.append("rsvp_option", toFlag(settings.acceptRsvp));
        fd.append("rsvp_family", toFlag(settings.acceptRsvp && settings.guestCount));
        fd.append("car_pool", attachments.carPool ? "1" : "0");
        fd.append("poll", attachments.poll ? "1" : "0");
        fd.append("schedule", attachments.schedule ? "1" : "0");
        fd.append("text_photo", attachments.textPhoto ? "1" : "0");
        fd.append("video_link", "");
        fd.append("ask", "");

        // placeholders for your API
        fd.append("members", "");
        fd.append("name", "");
        fd.append("names", "");
        fd.append("codes", "");
        fd.append("price", "");
        fd.append("payment", "");
        fd.append("emails", "");
        fd.append("phone", "");
        fd.append("phones", "");
        fd.append("permission_id", "1");
        fd.append("location_type", "1");
        fd.append("web_url", "");
        fd.append("vendors_list", "no");

        urlsToSend.forEach((u, i) => { if (u) fd.append(`p${i + 1}_url`, u); });

        const fileKeys = ["img", "img1", "img2"];
        filesToSend.forEach((file, i) => {
          const name = file?.name || `image_${i + 1}.jpg`;
          fd.append(fileKeys[i], file, name);
        });

        const res = await api.CreateInvitation(fd);
        const ok = res?.data?.status === "ok";
        const eventId = res?.data?.data?.event_id;

        if (ok && eventId) {
          toast(`Event created (ID: ${eventId})`, "success");
          resetForm();
          step.value = "type";
          selectedEventType.value = null;
          destroyPickers();
          router.push('/host');
        } else {
          const msg = res?.data?.msg || "Failed to create event.";
          const errs = res?.data?.errors;
          alertErr(errs?.length ? `${msg}: ${errs.join(", ")}` : msg);
        }
      } catch (e) {
        console.error("Form submit error:", e);
        const apiMsg = e?.response?.data?.msg || e?.response?.data?.message;
        const errs = e?.response?.data?.errors;
        alertErr(apiMsg ? (errs?.length ? `${apiMsg}: ${errs.join(", ")}` : apiMsg) : "Failed to create event.");
      } finally {
        isSubmitting.value = false;
      }
    };

    /* ---------- Mount ---------- */
    onMounted(async () => {
      step.value = "type";
      session.loadUser();
      await fetchEventTypes();
      fetchProfile();
      getCoHosts();

      await nextTick();
      initializeDefaultDates();

      // Load poster categories early
      await fetchCategories();

      // Init Maps
      try {
        await loadGoogleMapsAPI();
        if (window.google) {
          autocompleteService.value = new window.google.maps.places.AutocompleteService();
          placesService.value = new window.google.maps.places.PlacesService(document.createElement("div"));
          geocoder.value = new window.google.maps.Geocoder();
        }
      } catch (e) {
        console.error("Failed to init Maps", e);
      }

      if (step.value === "form") {
        await nextTick();
        initDatePickers();
      }
    });

    watch(() => session.userId, fetchProfile);
    watch(step, async (val) => {
      if (val === "form") {
        await nextTick();
        initDatePickers();
      }
    });

    watch(currentTab, async (t) => {
      if (t !== "Upload") await fetchMedia();
    });

    return {
      // state
      step, eventTypes, selectedEventType, loadingEventTypes,
      form, collapses, isSubmitting, previewImages,
      settings, showSettingsModal,
      attachments,
      all_day, include_end, start_date, start_time, end_date, end_time,
      locationSearch, locationSuggestions, showManualAddress, gettingLocation,
      search, currentTab, selectedCategory, categories, selected, selectedLocal,
      cohosts, selectedCohosts, user, hostNameDisplay, showCohostModal,
      // refs
      fileInput, startDateInput, endDateInput, startTimeInput, endTimeInput, modalCard,
      // computed
      selectedImagesCount, filteredMedia, dateValidation,
      // methods
      selectEventType, goToForm, toggleCollapse, toggleImagesCollapse,
      removeImage, removeLocalImage, changeTab, changeCategory,
      isSelected, toggleSelect, handleLocalUpload, addSelectedImages,
      onLocationSearch, selectLocation, getCurrentLocation,
      openCohostModal, closeCohostModal, saveCohostModal, cancelCohostModal,
      toggleCohost, removeCohost, isCohostSelected,
      submitForm,
      capitalize: (s) => (s ? String(s).replace(/\b\w/g, c => c.toUpperCase()) : ""),
    };
  },
};
</script>

<style scoped>
/* Event type tiles */
.page-bar{
  border:1px solid #eef1f6;
  border-radius:14px;
  background:#fff;
  box-shadow:0 8px 20px rgba(15,23,42,.06);
  padding:.85rem 1rem;
}
.page-title{font-weight:700;font-size:1.05rem;}
.card{border-radius:14px;border:1px solid #eef1f6;box-shadow:0 12px 26px rgba(15,23,42,.06);}
.btn-primary{border-radius:10px;font-weight:700;}
.btn-outline-primary{border-radius:10px;font-weight:700;}
.collapse.show{display:block;}
.upload-tile{
  border:2px dashed #cbd5e1;
  background:#f8fafc;
  border-radius:14px;
  padding:1rem;
  cursor:pointer;
  font-weight:700;
  color:#0f172a;
  transition:all .15s ease;
}
.upload-tile:hover{background:#eef2f7;border-color:#94a3b8;}
.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;}

.event-settings-card{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:1rem;
  padding:1rem;
  border:1px solid #e5e7eb;
  border-radius:12px;
  background:#fdfdff;
  box-shadow:0 10px 22px rgba(15,23,42,.06);
  cursor:pointer;
  transition:all .12s ease;
}
.event-settings-card:hover{box-shadow:0 12px 26px rgba(15,23,42,.09);transform:translateY(-1px);}

.settings-modal-card{
  width:min(720px, 100%);
  background:#fff;
  border-radius:14px;
  box-shadow:0 26px 64px rgba(15,23,42,.22);
  max-height:85vh;
  overflow:auto;
}
.settings-backdrop{align-items:flex-end;}
.settings-modal-header{
  display:flex;
  align-items:center;
  padding:12px 16px;
  border-bottom:1px solid #f1f5f9;
  position:sticky;
  top:0;
  background:#fff;
  z-index:2;
}
.settings-modal-body{padding:16px 16px 22px; display:flex; flex-direction:column; gap:16px; background:#fdf8ff;}
.settings-section{
  padding:16px;
  border-radius:16px;
  border:1px solid #f3e8ee;
  background:linear-gradient(180deg,#fff7f8 0%,#ffffff 70%);
  box-shadow:0 12px 26px rgba(15,23,42,.06);
}
.settings-section-title{font-weight:700;}
.settings-section-sub{margin:0 0 10px; color:#6b7280; font-size:.92rem;}
.settings-tile{
  display:flex; align-items:flex-start; justify-content:space-between; gap:16px; flex-wrap:wrap;
  background:#fff; border:1px solid #f1f5f9; border-radius:14px; padding:12px 14px;
  margin-bottom:12px;
}
.settings-tile:last-child{margin-bottom:0;}
.settings-tile > div:first-child{flex:1; min-width:200px;}
.settings-tile .switch{flex-shrink:0;}
.settings-tile-title{font-weight:700;}
.settings-tile-sub{color:#6b7280; font-size:.92rem; margin-bottom:0;}
.disabled-tile{opacity:.6;}
.settings-modal-card .switch{margin-left:8px;}
@media (max-width: 768px){
  .settings-modal-card{border-radius:14px 14px 0 0;}
  .settings-tile{flex-direction:column; align-items:flex-start;}
}

.event-type-tile{
  background:#f8fbff;
  border:1.5px solid #cfe3ff;
  border-radius:12px;
  padding:16px;
  transition:all .15s ease;
  width:100%;
}
.event-type-tile__body{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:.75rem;
  flex-wrap:wrap;
}
.event-type-tile__copy{
  flex:1;
  min-width:0;
}
.event-type-tile__copy .h5,
.event-type-tile__copy .small{
  word-break:break-word;
}
.event-type-tile__body .radio-badge{
  flex-shrink:0;
}
@media (max-width: 768px){
  .event-type-tile__body{
    gap:.5rem;
  }
  .event-type-tile__copy{
    min-width:60%;
  }
}
.event-type-tile:hover{ box-shadow:0 6px 20px rgba(16,24,40,.06); }
.event-type-tile.active{ background:#eaf2ff; border-color:#6ea8fe; }
.radio-badge{
  width:24px; height:24px; border:2px solid #8bb3ff; border-radius:50%;
  display:inline-flex; align-items:center; justify-content:center; background:#fff;
}
.radio-badge .dot{ width:12px; height:12px; background:#2c6bed; border-radius:50%; }

/* Tabs */
.tabset{ display:flex; gap:8px; border-bottom:1px solid #e9ecef; margin-bottom:1rem; flex-wrap:wrap; }
.tab-btn{ appearance:none; border:0; background:transparent; padding:.5rem .75rem; border-radius:.25rem .25rem 0 0; font-weight:600; color:#6c757d; cursor:pointer; }
.tab-btn.active{ color:#212529; background:#f8f9fa; border:1px solid #e9ecef; border-bottom-color:transparent; }

/* Inputs */
.switch{ position:relative; display:inline-block; width:50px; height:24px; }
.switch input{ opacity:0; width:0; height:0; }
.slider{ position:absolute; cursor:pointer; top:0; left:0; right:0; bottom:0; background-color:#ccc; transition:.4s; border-radius:24px; }
.slider:before{ position:absolute; content:""; height:18px; width:18px; left:3px; bottom:3px; background:#fff; transition:.4s; border-radius:50%; }
input:checked + .slider{ background:#4caf50; }
input:checked + .slider:before{ transform:translateX(26px); }

/* Helpers */
.category-tabs::-webkit-scrollbar { height:5px; }
.category-tabs::-webkit-scrollbar-thumb { background:#ccc; border-radius:10px; }
.cohost-chip{ max-width:210px; background-color:#cecece; border-radius:50px; }
.del-btn{ top:0; right:0; height:20px!important; width:20px!important; border-radius:50%; line-height:7px; padding:2px; }

/* Modal */
.modal-backdrop-custom{
  position:fixed; inset:0; background:rgba(15,23,42,.4); z-index:2000;
  display:flex; align-items:center; justify-content:center; padding:12px;
}
.modal-card{
  width:min(680px, 100%); background:#fff; border-radius:12px; box-shadow:0 24px 60px rgba(2,6,23,.25);
  max-height:80vh; overflow:auto; outline:0;
}
.modal-header, .modal-footer{ padding:12px 16px; border-bottom:1px solid #eee; }
.modal-footer{ border-top:1px solid #eee; border-bottom:0; }
.modal-title{ margin:0; }
.close{ border:0; background:transparent; font-size:24px; line-height:1; }
</style>
